import React from "react";
import { useOrder } from "../state/order";
import { useAuth } from "../state/auth";

interface BasketModalProps {
  open: boolean;
  onClose: () => void;
}

const BasketModal: React.FC<BasketModalProps> = ({ open, onClose }) => {
  const { items, totalPoints, add, removeOne, removeAllOf, clear } = useOrder();
  const { user } = useAuth();
  const userPoints = user?.points ?? 0;
  const remainingPoints = userPoints - totalPoints;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full md:w-1/2 h-full bg-white shadow-2xl border-l border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="text-lg font-semibold text-gray-900">Your Basket</div>
          <button
            aria-label="Close"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="h-[calc(100%-144px)] overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-gray-600">Your basket is empty.</div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.toy._id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={item.toy.images?.[0] || ""}
                      alt={item.toy.name}
                      className="w-14 h-14 rounded object-cover"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.toy.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {item.toy.points} pts each
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => removeOne(item.toy._id)}
                      className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-800"
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <div className="min-w-[2rem] text-center font-medium">
                      {item.quantity}
                    </div>
                    <button
                      onClick={() => add(item.toy, 1)}
                      disabled={item.quantity >= item.toy.stockQuantity}
                      className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                    <button
                      onClick={() => removeAllOf(item.toy._id)}
                      className="ml-2 text-red-600 hover:text-red-700"
                      aria-label="Remove item"
                      title="Remove"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span>
                  Your points:{" "}
                  <span className="font-semibold">{userPoints}</span>
                </span>
                <span>
                  In basket:{" "}
                  <span className="font-semibold">{totalPoints}</span>
                </span>
                <span>
                  Remaining:{" "}
                  <span
                    className={`font-semibold ${
                      remainingPoints < 0 ? "text-red-600" : ""
                    }`}
                  >
                    {remainingPoints}
                  </span>
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded overflow-visible">
                <div
                  className={`h-2 rounded ${
                    totalPoints > userPoints ? "bg-red-500" : "bg-brand-600"
                  }`}
                  style={{
                    width: userPoints
                      ? `${(totalPoints / userPoints) * 100}%`
                      : "0%",
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <button
                  onClick={clear}
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
              <button
                onClick={onClose}
                className="px-3 py-2 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasketModal;
